import type { FieldValues, Path, UseFormSetError } from 'react-hook-form'
import { z } from 'zod'

import { ApiError, errorMessage } from './api'

/**
 * Cola entre Zod (a fonte única de validação) e o react-hook-form.
 *
 * ---------------------------------------------------------------------------
 * POR QUE NÃO USAMOS UM "RESOLVER" QUE VALIDA O FORMULÁRIO INTEIRO
 * ---------------------------------------------------------------------------
 * Os schemas de `@locaveri/shared` TRANSFORMAM: `emailSchema` devolve minúsculo,
 * `telefoneSchema` devolve só dígitos, `dataDeNascimentoSchema` devolve `Date` e
 * `moneySchema` devolve `bigint`. Um resolver clássico entrega ao `onSubmit` o
 * valor JÁ transformado — e aí `JSON.stringify` de um `bigint` estoura, que é
 * exatamente o tipo de bug que só aparece no formulário de conta faturada, em
 * produção.
 *
 * Então o fluxo aqui é: o formulário guarda a ENTRADA (o que a pessoa digitou),
 * o Zod valida essa entrada, e o que viaja para a API é a entrada validada —
 * `z.input`, que é justamente a forma JSON-serializável do contrato. A API
 * revalida e normaliza do lado dela, que é onde a normalização tem que valer.
 */

/**
 * Validador de campo a partir de um schema. Use em
 * `register('cpf', { validate: validarCom(cpfSchema) })`.
 */
export function validarCom(schema: z.ZodType): (valor: unknown) => true | string {
  return (valor) => {
    const resultado = schema.safeParse(valor)

    if (resultado.success) {
      return true
    }

    return resultado.error.issues[0]?.message ?? 'valor inválido'
  }
}

/** Igual a `validarCom`, mas deixa o campo vazio passar (campo opcional). */
export function validarComSeInformado(schema: z.ZodType): (valor: unknown) => true | string {
  const validar = validarCom(schema)

  return (valor) => {
    if (valor === undefined || valor === null || valor === '') {
      return true
    }

    return validar(valor)
  }
}

/** Detalhe de validação como o filtro global da API o emite. */
const detalhesDeValidacaoSchema = z.array(
  z.object({
    campo: z.string(),
    codigo: z.string(),
    mensagem: z.string(),
  }),
)

function ehCampoDoFormulario<TValues extends FieldValues>(
  campo: string,
  campos: readonly Path<TValues>[],
): campo is Path<TValues> {
  return (campos as readonly string[]).includes(campo)
}

/**
 * Espalha os erros de um `safeParse` pelos campos do formulário.
 *
 * Devolve a mensagem que NÃO coube em campo nenhum (erro de refinamento entre
 * campos, por exemplo) para a tela mostrar no alerta do formulário. Erro que
 * não aparece em lugar nenhum é o pior desfecho possível de uma validação.
 */
export function aplicarErrosDoZod<TValues extends FieldValues>(
  erro: z.ZodError,
  setError: UseFormSetError<TValues>,
  campos: readonly Path<TValues>[],
): string | null {
  const sobras: string[] = []

  for (const issue of erro.issues) {
    const campo = issue.path.map(String).join('.')

    if (campo.length > 0 && ehCampoDoFormulario<TValues>(campo, campos)) {
      setError(campo, { type: 'validate', message: issue.message })
    } else {
      sobras.push(issue.message)
    }
  }

  return sobras.length > 0 ? sobras.join(' · ') : null
}

/**
 * Traduz o erro da API para o formulário: o que for de campo vira erro de
 * campo, o resto vira a mensagem do alerta. Sempre devolve texto em pt-BR —
 * nunca `null` quando houve erro de verdade.
 */
export function aplicarErrosDaApi<TValues extends FieldValues>(
  erro: unknown,
  setError: UseFormSetError<TValues>,
  campos: readonly Path<TValues>[],
): string {
  if (ApiError.is(erro) && erro.code === 'VALIDATION_FAILED') {
    const detalhes = detalhesDeValidacaoSchema.safeParse(erro.details)

    if (detalhes.success) {
      const sobras: string[] = []

      for (const detalhe of detalhes.data) {
        // A frase é montada ANTES da guarda: dentro do `else` o TypeScript já
        // estreitou `detalhe.campo` para "o que não é campo deste formulário",
        // que num genérico é `never`.
        const descricao = `${detalhe.campo}: ${detalhe.mensagem}`

        if (ehCampoDoFormulario<TValues>(detalhe.campo, campos)) {
          setError(detalhe.campo, { type: 'server', message: detalhe.mensagem })
        } else {
          sobras.push(descricao)
        }
      }

      return sobras.length > 0 ? sobras.join(' · ') : erro.message
    }
  }

  return errorMessage(erro)
}
